import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { FileDetailsStageComponent } from "@bitwarden/web-vault/app/components/add-balance-wizard/file-details-stage.component";
import {
  SelectAccountStageComponent,
  StatementAccountWithBalances,
} from "@bitwarden/web-vault/app/components/add-balance-wizard/select-account-stage.component";
import { AddTransactionStageComponent } from "@bitwarden/web-vault/app/components/add-balance-wizard/add-transaction-stage.component";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { transactionDataType } from "@bitwarden/web-vault/app/models/types/transactionData.types";

// Stage order constant (shared with the host so it can render the stage indicator)
export const STAGE_ORDER = ["file-details", "select-account", "add-transaction"] as const;

/** Payload emitted to the host when the user finalises the wizard (Complete / Save and add later). */
export interface AddBalanceWizardResult {
  fileName: string;
  file: File;
  statementFromDate: string;
  statementToDate: string;
  accounts: StatementAccountWithBalances[];
  transactions: transactionDataType[];
}

/** Parsed-statement prefill: pre-populates the wizard from an imported (mapped) statement. */
export interface AddBalanceWizardPrefill {
  account: AccountView;
  fromDate: string;
  toDate: string;
  openingBalance?: string;
  closingBalance?: string;
  transactions?: transactionDataType[];
}

@Component({
  selector: "app-add-balance-wizard",
  templateUrl: "./add-balance-wizard.component.html",
})
export class AddBalanceWizardComponent implements OnInit, OnChanges {
  @Input() uploadedFileName: string;
  @Input() selectedFile: File;
  /** When set (mapped statement import), pre-populates dates, account+balances and transactions. */
  @Input() prefill: AddBalanceWizardPrefill | null = null;
  @Output() fileNameChanged = new EventEmitter<string>();
  @Output() saveRequested = new EventEmitter<AddBalanceWizardResult>();
  /** Emits the active stage key whenever it changes, so the host can adapt its layout. */
  @Output() stageChanged = new EventEmitter<string>();

  @ViewChild(FileDetailsStageComponent) fileDetailsStage: FileDetailsStageComponent;
  @ViewChild(SelectAccountStageComponent) selectAccountStage: SelectAccountStageComponent;
  @ViewChild(AddTransactionStageComponent) addTransactionStage: AddTransactionStageComponent;

  private i18nService = inject(I18nService);

  // Stage management
  currentStage = signal<"file-details" | "select-account" | "add-transaction">("file-details");

  // Statement data
  statementFromDate: string;
  statementToDate: string;
  selectedAccounts: StatementAccountWithBalances[] = [];
  transactions: transactionDataType[] = [];

  // Button configurations
  backButtonOptions: ActionButton;
  nextButtonOptions: ActionButton;
  saveAndAddLaterButtonOptions: ActionButton;
  addTransactionButtonOptions: ActionButton;
  completeButtonOptions: ActionButton;

  constructor() {
    // Initialize button options
    this.backButtonOptions = new ActionButton({
      icon: "left-arrow",
      iconClass: "tw-h-5 tw-w-5",
      class: "secondary",
      onClick: this.goBack.bind(this),
    });

    // File Details stage CTA. fullClass overrides gloss-button's default sizing
    // with the Figma spec (340x56 pill, teal-500, shadow-button = "Button shadow new").
    this.nextButtonOptions = new ActionButton({
      text: this.i18nService.t("next"),
      fullClass:
        "tw-flex tw-flex-col tw-shrink-0 tw-justify-center tw-items-center tw-gap-1 " +
        "tw-h-14 tw-w-[340px] tw-max-w-full tw-px-12 tw-py-3 tw-rounded-[50px] tw-border-none " +
        "tw-bg-teal-500 tw-text-white tw-font-medium",
      onClick: this.goToSelectAccount.bind(this),
    });

    this.saveAndAddLaterButtonOptions = new ActionButton({
      text: this.i18nService.t("saveAndAddLater"),
      class: "secondary",
      onClick: this.saveAndAddLater.bind(this),
    });

    this.addTransactionButtonOptions = new ActionButton({
      text: this.i18nService.t("addTransaction"),
      class: "primary",
      onClick: this.goToAddTransaction.bind(this),
    });

    // Add Transaction stage CTA: finalize (save file + accounts + transactions) and close.
    this.completeButtonOptions = new ActionButton({
      text: this.i18nService.t("complete"),
      class: "primary",
      onClick: this.saveData.bind(this),
    });

    // Mirror the current stage out to the host so it can collapse the file-preview
    // pane on the add-transaction stage (which needs the full width).
    effect(() => this.stageChanged.emit(this.currentStage()));
  }

  ngOnInit(): void {
    // No initialization required; the wizard starts on the file-details stage.
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedFile && !changes.selectedFile.isFirstChange()) {
      this.resetWizardState();
      this.currentStage.set("file-details");
    }

    if (changes.prefill && changes.prefill.currentValue) {
      this.applyPrefill(changes.prefill.currentValue);
    }
  }

  /**
   * Seed the wizard from a mapped statement import. Dates come from the statement, so we
   * jump to the select-account stage; the account+balances and transactions are handed to
   * the respective stages via their prefill inputs. Transactions are also held here so the
   * "save and add later" path (which skips the add-transaction stage) still persists them.
   */
  private applyPrefill(prefill: AddBalanceWizardPrefill): void {
    this.statementFromDate = prefill.fromDate;
    this.statementToDate = prefill.toDate;
    this.selectedAccounts = [];
    this.transactions = prefill.transactions ?? [];
    this.currentStage.set("select-account");
  }

  private resetWizardState(): void {
    this.selectedAccounts = [];
    this.transactions = [];
  }

  // Stage management - single computed signal with just current index
  stageInfo = computed(() => {
    const currentIndex = STAGE_ORDER.indexOf(this.currentStage());
    return {
      currentIndex,
      isFileDetailsStage: currentIndex === 0,
      isSelectAccountStage: currentIndex === 1,
      isAddTransactionStage: currentIndex === 2,
    };
  });

  goToSelectAccount(): void {
    // Validate file details if coming from file-details stage
    if (this.stageInfo().isFileDetailsStage) {
      if (this.fileDetailsStage && !this.fileDetailsStage.validateForm()) {
        return;
      }

      if (!this.uploadedFileName) {
        return;
      }
      if (!this.statementFromDate || !this.statementToDate) {
        return;
      }
    }

    this.currentStage.set("select-account");
  }

  goBack(): void {
    const currentIndex = this.stageInfo().currentIndex;
    if (currentIndex > 0) {
      this.currentStage.set(STAGE_ORDER[currentIndex - 1]);
    }
  }

  async goToAddTransaction(): Promise<void> {
    // Don't advance to an empty Add Transaction stage: require at least one linked
    // account (auto-committing an in-progress one) before leaving Select Account.
    if (this.selectAccountStage && !(await this.selectAccountStage.ensureLinkedAccountsForNext())) {
      return;
    }
    this.currentStage.set("add-transaction");
  }

  async saveAndAddLater(): Promise<void> {
    // Saving the statement to accounts also requires at least one linked account.
    if (this.selectAccountStage && !(await this.selectAccountStage.ensureLinkedAccountsForNext())) {
      return;
    }
    this.saveData();
  }

  saveData(): void {
    // Commit any transaction row the user typed but didn't "Add" so it isn't dropped on
    // Complete. Blocks (with field errors) if a half-filled row is invalid.
    if (this.addTransactionStage && !this.addTransactionStage.commitPendingTransactions()) {
      return;
    }

    // Validate that we have all required data
    if (!this.selectedAccounts || this.selectedAccounts.length === 0) {
      return;
    }

    if (!this.statementFromDate || !this.statementToDate) {
      return;
    }

    // Prepare the upload data
    const uploadData = {
      fileName: this.uploadedFileName,
      file: this.selectedFile,
      statementFromDate: this.statementFromDate,
      statementToDate: this.statementToDate,
      accounts: this.selectedAccounts,
      transactions: this.transactions,
    };

    // Emit to the host (cabinet-file-upload). The host performs the async save and closes
    // the overlay only after a confirmed save — see handleSaveAndAddLater.
    this.saveRequested.emit(uploadData);
  }

  onAccountsSelected(accounts: StatementAccountWithBalances[]): void {
    this.selectedAccounts = accounts;
  }

  onTransactionsChanged(transactions: transactionDataType[]): void {
    this.transactions = transactions;
  }

  getLinkedAccounts(): AccountView[] {
    // Extract just the account objects from selectedAccounts
    return this.selectedAccounts.map((acc) => acc.account);
  }

  onFileNameChange(newFileName: string): void {
    this.uploadedFileName = newFileName;
    this.fileNameChanged.emit(newFileName);
  }

  onStatementDatesSelected(dates: { fromDate: string; toDate: string }): void {
    this.statementFromDate = dates.fromDate;
    this.statementToDate = dates.toDate;
  }
}
